import { Divider, type FormItemProps, Select as AntdSelect, type SelectProps, Space, Table } from 'antd';
import { useField } from 'formik';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type UserGroupListDataFragment } from '../../../api/fragments/UserGroupListData';
import {
    type ListAvailableUserGroupsQuery,
    useListAvailableUserGroupsQuery,
} from '../../../api/queries/listAvailableUserGroups';
import { Select } from '../../../components/fields/SelectField';
import TableField from '../../../components/fields/TableField';

type Record = ListAvailableUserGroupsQuery['page']['items'][number];

export type SelectUserGroupsWithSearchFieldProps = Omit<SelectProps, 'options' | 'children'> & {
    name: string;
    userGroups?: UserGroupListDataFragment[];
    displayName?: string;
    description?: string;
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'required' | 'children'>;
    userGroupId?: string;
};

const filterOption: SelectProps['filterOption'] = (inputValue, option) =>
    option.children.toString().toLowerCase().includes(inputValue.toLowerCase());

const SelectUserGroupsWithSearchField = ({
    userGroups: userGroupsFromProps,
    name,
    displayName,
    description,
    itemProps,
    userGroupId,
    ...props
}: SelectUserGroupsWithSearchFieldProps) => {
    const { t } = useTranslation(['userGroupDetails']);

    const { data, loading } = useListAvailableUserGroupsQuery({
        fetchPolicy: 'cache-and-network',
        skip: !!userGroupsFromProps,
    });

    const userGroups = useMemo(() => userGroupsFromProps || data?.page.items || [], [data, userGroupsFromProps]);

    const [userGroupOptions, setUserGroupOptions] = useState<UserGroupListDataFragment[]>(userGroups);
    const [selectedUserGroups, setSelectedUserGroups] = useState<UserGroupListDataFragment[]>(userGroups);
    const [{ value: selectedUserGroupIds }, , { setValue }] = useField({ name });

    useEffect(() => {
        setUserGroupOptions(userGroups.filter(userGroup => !selectedUserGroupIds.includes(userGroup.id)));
        setSelectedUserGroups(userGroups.filter(userGroup => selectedUserGroupIds.includes(userGroup.id)));
    }, [userGroups, selectedUserGroupIds]);

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Divider orientation="left" orientationMargin="0">
                {t('userGroupDetails:parentUserGroupDrawer.title', { returnObjects: true })}
            </Divider>
            <Select
                filterOption={filterOption}
                onChange={newValue => setValue(newValue)}
                placeholder={t('userGroupDetails:parentUserGroupDrawer.placeholder')}
                {...props}
            >
                {userGroupOptions.map(userGroup => (
                    <AntdSelect.Option key={userGroup.id} label={userGroup.displayName} value={userGroup.id}>
                        {userGroup.displayName}
                    </AntdSelect.Option>
                ))}
            </Select>

            <Divider orientation="left" orientationMargin="0" plain>
                {t('userGroupDetails:parentUserGroupDrawer.parentUserGroupSelected', { returnObjects: true })}
            </Divider>
            <TableField
                dataSource={selectedUserGroups}
                loading={loading && !selectedUserGroupIds.length}
                name={name}
                rowKey="id"
                required
            >
                <Table.Column<Record>
                    dataIndex="displayName"
                    onFilter={(value: string, record) => new RegExp(value, 'i').test(record.displayName)}
                    sorter={(a, b) => a.displayName.localeCompare(b.displayName)}
                    title={t('userGroupDetails:parentUserGroupDrawer.columns.displayName')}
                    filterSearch
                />
                <Table.Column<Record>
                    dataIndex="description"
                    onFilter={(value: string, record) => new RegExp(value, 'i').test(record.description)}
                    sorter={(a, b) => a.description.localeCompare(b.description)}
                    title={t('userGroupDetails:parentUserGroupDrawer.columns.description')}
                    filterSearch
                />
            </TableField>
        </Space>
    );
};

export default memo(SelectUserGroupsWithSearchField);
